import { useEffect, useRef, useState } from 'react';
import { accountScope, ProgramModel, type ProgramModel as ProgramModelT } from '@heraion/shared';

import type { ProgramApiFetch } from '../ProgramEditor/types';
import { useAppActive } from '../useAppActive';
import { asyncStorageResourceCache } from '../Logger/state/asyncListCache';
import { parseTemplateItems, TEMPLATE_PATHS } from './useTemplates';

// #356: the model a followed program was written from, for «More time».
// Network first: a model changes under a migration, and a cache read first
// would keep the old one. The device copy answers only when a read fails
// (offline). Ours and the athlete's are two reads, as in NextBlockSheet.

const cacheOf = (modelId: string) =>
  asyncStorageResourceCache<unknown>(`templates/model/${modelId}`);

export async function loadProgramModel(
  modelId: string,
  apiFetch: ProgramApiFetch,
): Promise<ProgramModelT | null> {
  // #398: the account these reads are for; the copy is written under it.
  const scope = accountScope();
  const reads = await Promise.allSettled([
    apiFetch<unknown>(TEMPLATE_PATHS.model),
    apiFetch<unknown>(TEMPLATE_PATHS.mine),
  ]);
  const answered = reads.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
  const cache = cacheOf(modelId);
  const found = answered
    .flatMap((raw) => parseTemplateItems(raw, ProgramModel, 'model'))
    .find((m) => m.id === modelId);
  if (found) {
    void cache.write(found, scope);
    return found;
  }
  // Both answered and neither has it: the model is gone.
  if (answered.length === reads.length) return null;
  const parsed = ProgramModel.safeParse(await cache.read());
  return parsed.success ? parsed.data : null;
}

/**
 * The model for `modelId` (network, else the device copy), read once per id.
 * `loading` is true while a read is pending; without an id or an api nothing
 * is read and `model` is undefined. apiFetch is held by ref, as useTemplates:
 * a fresh literal per render must not read again. A read that found nothing
 * (offline, no device copy) is tried again when the app comes back to the
 * foreground (#467); one that found a model is not.
 */
export function useProgramModel(
  modelId: string | undefined,
  apiFetch: ProgramApiFetch | undefined,
): { model: ProgramModelT | undefined; loading: boolean } {
  const [read, setRead] = useState<{ id: string; model: ProgramModelT | null } | null>(null);
  const apiRef = useRef(apiFetch);
  apiRef.current = apiFetch;
  const hasApi = apiFetch !== undefined;
  const active = useAppActive();
  const haveRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const api = apiRef.current;
    if (!modelId || !api || !active) return;
    if (haveRef.current === modelId) return;
    let alive = true;
    void loadProgramModel(modelId, api)
      .catch(() => null)
      .then((m) => {
        if (!alive) return;
        haveRef.current = m ? modelId : undefined;
        setRead({ id: modelId, model: m });
      });
    return () => {
      alive = false;
    };
  }, [modelId, hasApi, active]);
  const done = read !== null && read.id === modelId;
  return {
    model: done ? (read.model ?? undefined) : undefined,
    loading: modelId !== undefined && hasApi && !done,
  };
}
